<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-card-container">
    <oxd-text tag="h6" class="orangehrm-main-title"
      >Interview with Anna</oxd-text
    >
    <oxd-text tag="p" class="anna-script-intro">
      Anna asks the shared questions (ANNA Settings → Interview) and this role's
      own, as written.
    </oxd-text>
    <oxd-divider />
    <oxd-text v-if="loaded && !options" tag="p" class="anna-script-warn">
      ANNA can't be reached right now, so the tones of voice aren't shown. The
      role questions can still be saved.
    </oxd-text>

    <oxd-form :loading="isLoading" @submit-valid="onSave">
      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item v-if="options">
            <oxd-input-field
              v-model="personality"
              type="select"
              label="Tone of voice"
              :options="personalityOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="preset"
              type="select"
              label="Start from an example role"
              :options="presetOptions"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-form-row>
        <oxd-text tag="p" class="anna-script-label"
          >Sections Anna uses</oxd-text
        >
        <div class="anna-script-blocks">
          <oxd-input-field
            v-for="b in blockList"
            :key="b.key"
            v-model="blocks[b.key]"
            type="switch"
            :option-label="b.label"
          />
        </div>
      </oxd-form-row>

      <oxd-form-row class="anna-script-questions">
        <oxd-input-field
          v-model="roleQuestions"
          type="textarea"
          label="This role's questions (one per line)"
          placeholder="Tell me about a time you …"
        />
        <oxd-text tag="p" class="anna-script-help">
          None: Anna skips the role part.
        </oxd-text>
      </oxd-form-row>

      <oxd-form-actions>
        <oxd-button
          display-type="secondary"
          label="Save interview"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {DASHBOARD_ROLES} from './dashboard-roles';

// A vacancy's HR interview: which blocks Anna uses, its own questions (asked as written) and its personality.
// No switch for the role block: it's on exactly when the role has questions.
const BLOCKS = [
  {key: 'opening', label: 'General'},
  {key: 'resume', label: 'The resume'},
  {key: 'deepDive', label: 'Personality'},
  {key: 'screening', label: 'Practical questions'},
  {key: 'ending', label: 'Ending'},
];
const allOn = () => Object.fromEntries(BLOCKS.map((b) => [b.key, true]));

export default {
  name: 'AnnaVacancyScript',
  props: {
    vacancyId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/anna/vacancies/${props.vacancyId}/script`,
    );
    return {http};
  },
  data() {
    return {
      isLoading: false,
      loaded: false,
      options: null,
      roleQuestions: '',
      blockList: BLOCKS,
      // a block not saved is on
      blocks: allOn(),
      personality: null,
      preset: null,
      // as saved: kept while ANNA (and so the personalities) can't be shown
      savedPersonality: null,
      presetOptions: DASHBOARD_ROLES.map((r) => ({id: r.role, label: r.role})),
    };
  },
  computed: {
    personalityOptions() {
      const fallback = this.options?.defaultPersonality;
      return [
        {id: '', label: `Default${fallback ? ` (${fallback})` : ''}`},
        ...(this.options?.personalities || []).map((p) => ({id: p, label: p})),
      ];
    },
  },
  watch: {
    preset(option) {
      const role = option && DASHBOARD_ROLES.find((r) => r.role === option.id);
      if (!role) return;
      this.roleQuestions = role.roleQuestions.join('\n');
      this.blocks = {...allOn(), ...role.blocks};
      this.personality = this.personalityOption(role.personality);
    },
  },
  created() {
    this.load();
  },
  methods: {
    personalityOption(name) {
      return (
        this.personalityOptions.find((p) => p.id && p.id === name) ||
        this.personalityOptions[0]
      );
    },
    apply(script) {
      this.savedPersonality = script.personality;
      this.roleQuestions = (script.roleQuestions || []).join('\n');
      this.blocks = {...allOn(), ...(script.blocks || {})};
      this.personality = this.personalityOption(script.personality);
    },
    load() {
      this.isLoading = true;
      this.http
        .request({method: 'GET'})
        .then(({data: {data}}) => {
          this.options = data.options;
          this.apply(data);
          this.loaded = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onSave() {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            roleQuestions: this.roleQuestions.split('\n'),
            blocks: {...this.blocks, role: this.roleQuestions.trim() !== ''},
            personality: this.options
              ? this.personality?.id || null
              : this.savedPersonality,
          },
        })
        .then(({data: {data}}) => {
          this.apply(data);
          this.preset = null;
          return this.$toast.saveSuccess();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-script-intro,
.anna-script-help {
  color: #6a7482;
  font-size: 13px;
  line-height: 1.6;
}
.anna-script-help {
  margin-top: 4px;
  font-size: 12px;
}
.anna-script-label {
  font-size: 13px;
  font-weight: 600;
  color: #12233f;
  margin-bottom: 6px;
}
.anna-script-blocks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 32px;
  // each switch as wide as its label, not a full-width row
  > :deep(.oxd-input-group) {
    flex: 0 0 auto;
    width: auto;
  }
}
// a long list grows down, with its own scroll, never wider than the card
.anna-script-questions :deep(textarea) {
  min-height: 120px;
  max-height: 40vh;
  resize: vertical;
  overflow: auto;
}
.anna-script-warn {
  color: #a05a00;
  font-size: 13px;
  margin-bottom: 12px;
}
</style>
